/** @jsxImportSource @emotion/react */
'use memo'

import styled from '@emotion/styled'
import type { ColDef } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistrationDivision } from '@sportninja/common/features/organization/registration/types'
import { CHECKOUT_TAXES_AND_FEES_NOTE } from '@sportninja/common/features/registration/constants/checkoutCopy'
import DataGrid from 'src/components/ts-components/DataGrid'
import Text from 'src/components/ts-components/Text'
import { useDivisionColumns } from '../hooks/useDivisionColumns'

interface Props {
  divisions: OrgRegistrationDivision[]
  /** See `OrgRegistrationCardModel.inactiveAvailabilityLabel`. */
  inactiveAvailabilityLabel: string
}

/** Tall enough for a fee with its deposit line under it. */
const ROW_HEIGHT = 56

/** Single-line cells centred in the taller row, level with the two-line fee cells. */
const CENTERED_CELL: ColDef<OrgRegistrationDivision> = {
  cellStyle: { display: 'flex', alignItems: 'center' },
}

/**
 * A registration's divisions, with player and team registration side by side.
 * Mounted only while its card is expanded, so the page holds one grid per open
 * card — not one per card. On a card narrower than the columns' minimum widths
 * the table scrolls sideways. The fees are pre-tax, so the same checkout note
 * the registration flow shows sits under the table.
 */
const OrgRegistrationDivisionGrid = ({
  divisions,
  inactiveAvailabilityLabel,
}: Props) => {
  'use memo'

  const columnDefs = useDivisionColumns(inactiveAvailabilityLabel)

  return (
    <Wrapper>
      <DataGrid<OrgRegistrationDivision>
        rowData={divisions}
        columnDefs={columnDefs}
        getRowId={({ data }) => data.id}
        domLayout='autoHeight'
        // AG Grid's 150px floor is room for the no-rows overlay; the card
        // never mounts this grid without divisions, so it is only dead space.
        autoHeightMinBodyHeight={0}
        rowHeight={ROW_HEIGHT}
        defaultColDef={CENTERED_CELL}
        suppressCellFocus
      />
      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_50}
      >
        {CHECKOUT_TAXES_AND_FEES_NOTE}
      </Text>
    </Wrapper>
  )
}

export default OrgRegistrationDivisionGrid

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
`
